import React from "react";
import { toRelativeUrl } from "@okta/okta-auth-js";
import { Security, LoginCallback } from "@okta/okta-react";
import "./App.css";
import { Routes, Route, useNavigate } from "react-router-dom";
import {
  bridge,
  buildOktaAuth
} from "@bridge/okta-auth";
import DataExplorer from "./components/DataExplorer/DataExplorer.jsx";
import { RequiredAuth } from "./components/SecureRoute";
import { Logout } from "./components/Logout";
import SmokeTests from "./components/SmokeTests/SmokeTests.jsx";
import Events from "./components/Events/Events.jsx";
import GroupSearch from "./components/GroupSearch/GroupSearch.jsx";
import GlobalHeader from "./components/GlobalHeader";
import { DealerContextProvider } from "./components/DataExplorer/DealerContext.jsx";
import VersionNumber from "./components/VersionNumber";
import useApi from "./lib/apiService";
import LaunchDarklyProvider from "./components/LaunchDarklyProvider";
import BridgeBar from "@common-core/react-bridge-bar";

// Authenticate with either a predefined Bridge auth environment, or
// with a custom IDP configuration.
const idp = import.meta.env.VITE_BRIDGE_ENV
  ? bridge(import.meta.env.VITE_BRIDGE_ENV)
  : {
      key: import.meta.env.VITE_IDP_KEY,
      issuer: import.meta.env.VITE_IDP_ISSUER,
      authorizeUrl: import.meta.env.VITE_IDP_AUTHORIZE_URL,
      tokenUrl: import.meta.env.VITE_IDP_TOKEN_URL,
    };

const oktaAuth = buildOktaAuth({
  idp,
  clientId: import.meta.env.VITE_BRIDGE_CLIENT_ID,
  scopes: ["enterprise.dealer-inventory-vehicle.vehicle-data-explorer.api.read"],
  renewTokensOnExpiration: true,
  redirectUri: `${window.location.origin}/callback`
});

function AppContent() {
  const [username, setUsername] = React.useState("");
  const [addDealerToRecents, setAddDealerToRecents] = React.useState(null);
  const [isNickLargent, setIsNickLargent] = React.useState(false);
  const Api = useApi();

  React.useEffect(() => {
    async function fetchUser() {
      try {
        const user = await Api.getUserInfo();
        console.log("Got username", user);
        setUsername(user.username);
        setIsNickLargent(user.username === "Nick-Largent");

        const pendoData = {
          visitor: {
            id: user.username,
            email: user.username,
          },
          account: {
            id: `cvi-vehicle-data-explorer-${import.meta.env.VITE_BRIDGE_ENV}`,
          }
        };
        if (window.pendo) {
          window.pendo.initialize(pendoData);
          console.log("Pendo initialized", pendoData);
        } 
        
      } catch (e) {
        console.log("Error fetching user info", e);
        setUsername("");
        setIsNickLargent(false);
      }
    }
    fetchUser();
  }, [Api]);

  const handleSearchSubmit = (addToRecentsFn, searchTerm) => {
    setAddDealerToRecents(() => (dealer) => {
      addToRecentsFn(dealer, searchTerm);
    });
  };

  return (
    <DealerContextProvider>
      <GlobalHeader username={username} onSearchSubmit={handleSearchSubmit} isNickLargent={isNickLargent} />
      <Routes>
        <Route path="/" element={<RequiredAuth />}>
          <Route path="" element={<DataExplorer addDealerToRecents={addDealerToRecents} username={username} isNickLargent={isNickLargent} />} />
          {isNickLargent && <Route path="/test" element={<SmokeTests />} />}
          {isNickLargent && <Route path="/events" element={<Events />} />}
          <Route path="/group-search" element={<GroupSearch />} />
        </Route>
        <Route path="/callback" element={<LoginCallback />} />
        <Route path="/logout" element={<Logout />} />
      </Routes>
      <div style={{marginTop: 32, marginBottom: 16, textAlign: 'center', color: 'gray', fontSize: '10pt' }}>
        <VersionNumber />
      </div>
    </DealerContextProvider>
  );
}

function App() {
  const navigate = useNavigate();
  const restoreOriginalUri = (_oktaAuth, originalUri) => {
    navigate(toRelativeUrl(originalUri || '/', window.location.origin));
  };

  const bridgeBarEnv = (import.meta.env.VITE_BRIDGE_ENV === 'live-prod') ? 'prod' : 'qa';
  var solutionGroupCode = import.meta.env.VITE_SOLUTION_GROUP_CODE;

  const sessionId = Math.random().toString();
  const handleLoad = (e) => {
    e.preventDefault();
    setDealerId(dealerIdInput);
  }

  return (
    <div className="App">
      <Security oktaAuth={oktaAuth}
      restoreOriginalUri={restoreOriginalUri}>
        <BridgeBar options={{
          env: bridgeBarEnv,
          solutionGroupCode: solutionGroupCode,
          sessionId: sessionId,
          language: "en"
        }} />
        <LaunchDarklyProvider>
          <AppContent />
        </LaunchDarklyProvider>
      </Security>
    </div>
  );
}

export default App;
